import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, MoreVertical, Settings, ShieldCheck } from 'lucide-react';
import { Avatar, Button, IconButton, Menu, Select } from '../ui';
import { useAuth } from '../../context/AuthContext';
import { canOpenAdmin } from '../../utils/adminAccess.js';
import { useTheme } from '../../context/ThemeContext.jsx';

const LANGUAGES = ['de', 'en'];

/**
 * The sidebar user area (spec §8.2): identity, theme, language, settings,
 * administration (admin role only) and sign-out.
 *
 * On the tablet icon rail everything except the avatar/menu button collapses
 * away — the same actions stay reachable through the menu.
 */
export function UserMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { choice, setChoice } = useTheme();

  const isAdmin = canOpenAdmin(user);
  const displayName = user?.username || user?.email || t('nav.userMenu');
  const nextLanguage = LANGUAGES.find((lng) => lng !== (i18n.language || 'de').slice(0, 2)) ?? 'en';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const items = [
    {
      id: 'settings',
      label: t('nav.settings'),
      icon: <Settings size={16} aria-hidden="true" />,
      onSelect: () => navigate('/settings'),
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: t('nav.admin'),
            icon: <ShieldCheck size={16} aria-hidden="true" />,
            onSelect: () => navigate('/admin'),
          },
        ]
      : []),
    { type: 'separator' },
    {
      id: 'logout',
      label: t('auth.logout'),
      icon: <LogOut size={16} aria-hidden="true" />,
      tone: 'danger',
      onSelect: handleLogout,
    },
  ];

  return (
    <div className="etch-shell-user">
      <div className="etch-shell-user__identity">
        <Avatar name={displayName} />
        <span className="etch-shell-user__meta etch-shell-desktop-only">
          <span className="etch-shell-user__name">{displayName}</span>
          {user?.email && user?.username ? (
            <span className="etch-shell-user__mail">{user.email}</span>
          ) : null}
        </span>
        <Menu
          align="end"
          items={items}
          trigger={(triggerProps) => (
            <IconButton {...triggerProps} label={t('nav.userMenu')}>
              <MoreVertical size={18} aria-hidden="true" />
            </IconButton>
          )}
        />
      </div>

      <div className="etch-shell-user__controls etch-shell-desktop-only">
        <Select
          label={t('settings.theme.title')}
          hideLabel
          className="etch-shell-user__theme"
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
          options={[
            { value: 'auto', label: t('settings.theme.auto') },
            { value: 'paper', label: t('settings.theme.paper') },
            { value: 'ink', label: t('settings.theme.ink') },
          ]}
        />
        <Button
          variant="ghost"
          size="compact"
          aria-label={`${t('settings.language')}: ${nextLanguage.toUpperCase()}`}
          onClick={() => i18n.changeLanguage(nextLanguage)}
        >
          {nextLanguage.toUpperCase()}
        </Button>
      </div>
    </div>
  );
}

export default UserMenu;
